import { useMemo, useState } from 'react'
import { Search, Trash2 } from 'lucide-react'
import { Input } from '@/components/ui/Input'
import { Badge } from '@/components/ui/Badge'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { Pagination } from '@/components/ui/Pagination'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useDataTableExport } from '@/hooks/useDataTableExport'
import { useAuthority } from '@/hooks/useAuthority'
import { type PageSize } from '@/hooks/useCrudList'
import type { PaginationMeta } from '@/types/api'
import { normalizeForMatch } from '@/lib/validators'
import { CredentialingProvidersService } from '@/services/modules/CredentialingService'
import { toast } from '@/components/ui/Toast'

/** Linha exibida na tabela de especialidades/exames de um credenciado. */
export interface STRow {
  pivotId: string
  name: string
  /** `member` = quanto o associado paga na clínica. */
  formatted: { price: string; discount: string; copay: string; member: string }
  telemedicine?: boolean
}

/**
 * Tabela (filtro + paginação + exportação + excluir) de especialidades OU
 * exames de um credenciado. Reusada no form de edição e no modal "ver" do
 * kanban. NÃO inclui o formulário de adição — esse vive só no form de edição.
 */
export function SpecialtyTestTable({ kind, providerId, rows, onChanged }: {
  kind: 'specialty' | 'test'
  providerId: string
  rows: STRow[]
  onChanged: () => void | Promise<void>
}) {
  const isSpec = kind === 'specialty'
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState<PageSize>(25)
  const [deleting, setDeleting] = useState<STRow | null>(null)
  const { can } = useAuthority()
  // A rota de remoção (specialty-or-test) é a mesma para especialidade e exame
  // e o backend exige `manage_specialties` nas duas.
  const canRemove = can('credentialing.providers.manage_specialties')

  const filtered = useMemo(() => {
    const q = normalizeForMatch(search)
    return q ? rows.filter(r => normalizeForMatch(r.name).includes(q)) : rows
  }, [rows, search])
  const lastPage = Math.max(1, Math.ceil(filtered.length / pageSize))
  const safePage = Math.min(page, lastPage)
  const paged = filtered.slice((safePage - 1) * pageSize, safePage * pageSize)
  const meta: PaginationMeta = {
    current_page: safePage, per_page: pageSize, last_page: lastPage, total: filtered.length,
    from: filtered.length ? (safePage - 1) * pageSize + 1 : null,
    to: Math.min(safePage * pageSize, filtered.length) || null,
  }

  const columns: DataTableColumn<STRow>[] = [
    {
      key: 'name', header: isSpec ? 'Especialidade' : 'Exame', alwaysVisible: true,
      render: r => <span className="font-medium text-gray-800">{r.name}</span>,
    },
    ...(isSpec ? [{
      key: 'telemedicine', header: 'Telemedicina', align: 'center', className: 'w-32',
      render: (r: STRow) => r.telemedicine
        ? <Badge variant="green">Atende</Badge>
        : <Badge variant="gray">Não atende</Badge>,
    } as DataTableColumn<STRow>] : []),
    { key: 'price',    header: 'Valor',          align: 'center', className: 'w-32', render: r => <span className="text-gray-600">{r.formatted.price}</span> },
    { key: 'discount', header: 'Desconto',       align: 'center', className: 'w-32', render: r => <span className="text-gray-600">{r.formatted.discount}</span> },
    { key: 'copay',    header: 'Coparticipação', align: 'center', className: 'w-36', render: r => <span className="text-gray-600">{r.formatted.copay}</span> },
    { key: 'member',   header: 'Valor associado', align: 'center', className: 'w-36', render: r => <span className="font-medium text-gray-800">{r.formatted.member}</span> },
  ]

  const exp = useDataTableExport<STRow>({
    filename: isSpec ? 'especialidades' : 'exames',
    service: { list: async () => ({ data: filtered }) },
    columns: [
      { header: isSpec ? 'Especialidade' : 'Exame', accessor: r => r.name },
      ...(isSpec ? [{ header: 'Telemedicina', accessor: (r: STRow) => r.telemedicine ? 'Atende' : 'Não atende' }] : []),
      { header: 'Valor',          accessor: r => r.formatted.price },
      { header: 'Desconto',       accessor: r => r.formatted.discount },
      { header: 'Coparticipação', accessor: r => r.formatted.copay },
      { header: 'Valor associado', accessor: r => r.formatted.member },
    ],
  })

  async function confirmDelete() {
    if (!deleting) return
    try {
      await CredentialingProvidersService.removeSpecialtyOrTest(providerId, deleting.pivotId, kind)
      toast.success({ title: 'Removido', description: isSpec ? 'Especialidade removida.' : 'Exame removido.' })
      setDeleting(null)
      await onChanged()
    } catch { toast.error('Erro ao remover.') }
  }

  return (
    <>
      <div className="mb-3 max-w-xs">
        <Input
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1) }}
          placeholder={isSpec ? 'Filtrar especialidade' : 'Filtrar exame'}
          iconLeft={<Search size={14} />}
          clearable
        />
      </div>

      <DataTable
        tableId={isSpec ? 'credentialing.provider.specialties' : 'credentialing.provider.tests'}
        columns={columns}
        rows={paged}
        getRowKey={r => r.pivotId}
        emptyMessage={isSpec ? 'Nenhuma especialidade cadastrada.' : 'Nenhum exame cadastrado.'}
        exportable
        onExportPage={() => exp.exportPage(paged)}
        onExportAll={exp.exportAll}
        rowActions={canRemove ? (r) => [
          { key: 'remove', label: 'Remover', icon: <Trash2 size={14} />, danger: true, onClick: () => setDeleting(r) },
        ] : undefined}
      />
      <Pagination meta={meta} size={pageSize} onChange={setPage} onSizeChange={setPageSize} />

      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        title={isSpec ? 'Remover especialidade?' : 'Remover exame?'}
        description={`Remover "${deleting?.name}" deste credenciado?`}
        confirmLabel="Remover"
        variant="danger"
      />
    </>
  )
}
